/**
 * @fileoverview Enhanced providers setup for Web3 wallet connections and social authentication via Dynamic.xyz
 * @module lib/providers
 */

import type { ReactElement, ReactNode } from 'react';
import { useMemo, useCallback } from 'react';
import { WagmiProvider } from 'wagmi';
import type { Chain } from 'wagmi/chains';
import { avalanche, avalancheFuji, mainnet, arbitrum } from 'wagmi/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DynamicContextProvider } from '@dynamic-labs/sdk-react-core';
import { DynamicWagmiConnector } from '@dynamic-labs/wagmi-connector';
import { EthereumWalletConnectors } from '@dynamic-labs/ethereum';
import { http, createConfig } from 'wagmi';
import { AuthProvider } from './auth';
import { getEvmNetworks, dynamicEnvironmentId, dynamicCssOverrides } from './dynamic';
import { isMobileDevice } from '@/shared/utils/device';

interface NetworkConfig {
  chain: Chain;
  contracts: {
    fundraiserFactory: `0x${string}`;
    investmentsTokens: {
      usdt: `0x${string}`;
      usdc: `0x${string}`;
    };
  };
}

// All supported testnet chains go here
export const testnetConfig: Record<string, NetworkConfig> = {
  avax: {
    chain: avalancheFuji,
    contracts: {
      fundraiserFactory: '0x3F3A34F35fFf144AC74ED4C1E437fF4836697CeD',
      investmentsTokens: {
        // Fuji doesn't have a canonical USDT deployment; reusing the Circle USDC
        // address as a placeholder so the form still works on testnet.
        usdt: '0x5425890298aed601595a70AB815c96711a31Bc65',
        usdc: '0x5425890298aed601595a70AB815c96711a31Bc65',
      }
    }
  },
}

// All supported mainnet chains go here
// Token addresses (Avalanche C-Chain):
// - USDT.e: https://snowtrace.io/token/0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7
// - USDC.e: https://snowtrace.io/token/0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E
export const mainnetConfig: Record<string, NetworkConfig> = {
  avax: {
    chain: avalanche,
    contracts: {
      fundraiserFactory: '0x2c5F0b6893F3a931a7f9c4D4Af0b2c040791571E',
      investmentsTokens: {
        usdt: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
        usdc: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
      }
    }
  },
}

// Defined in .env: can either be mainnet or testnet (default)
const networkEnv = import.meta.env.VITE_NETWORK || 'testnet';
export const networkConfig = networkEnv === 'testnet' ? testnetConfig : mainnetConfig;

/**
 * Supported chains for wallet connection.
 *
 * We include multiple chains to avoid unnecessary chain switch requests on mobile.
 * Since wallet addresses are chain-agnostic, users can connect from any EVM chain
 * and we'll capture the same address. The primary chain (Avalanche) is first for transactions.
 */
const supportedChains: [Chain, ...Chain[]] = networkEnv === 'testnet'
  ? [avalancheFuji]
  : [avalanche, mainnet, arbitrum];

/**
 * React Query client for caching and state management.
 *
 * Configured with sensible defaults for Web3 applications:
 * - 5 minute stale time for blockchain data
 * - 10 minute cache time for optimal performance
 * - Disabled automatic refetching to prevent unnecessary network calls
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnMount: false, // Don't refetch on mount - use cached data if available
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: false, // Don't refetch on network reconnect
    },
  },
});

/**
 * Wagmi configuration for blockchain interactions.
 *
 * Dynamic handles wallet connection, but we still need wagmi config
 * for contract interactions and state management.
 */
export const config = createConfig({
  chains: supportedChains,
  multiInjectedProviderDiscovery: false,
  transports: {
    [avalancheFuji.id]: http(),
    [avalanche.id]: http(),
    [mainnet.id]: http(),
    [arbitrum.id]: http(),
  },
});

/**
 * Props for the Providers component.
 */
interface ProvidersProps {
  /** Child components to be wrapped with Web3 providers */
  children: ReactNode;
}

/**
 * Root providers component that sets up the Web3, React Query, and authentication context.
 *
 * Provides the necessary context for:
 * - Wallet connection via Dynamic.xyz
 * - Social authentication via Dynamic.xyz (Google, Twitter, Email)
 * - Embedded wallets for users without external wallets
 * - Blockchain interactions via Wagmi
 * - Server state management via TanStack Query
 * - User profile sync with Supabase
 *
 * Provider nesting order is important:
 * 1. DynamicContextProvider - Unified auth (wallet + social)
 * 2. WagmiProvider - Web3 wallet functionality
 * 3. QueryClientProvider - Server state management
 * 4. DynamicWagmiConnector - Bridges Dynamic to Wagmi
 * 5. AuthProvider - Enhanced authentication with Supabase integration
 *
 * @component
 * @example
 * ```tsx
 * <Providers>
 *   <App />
 * </Providers>
 * ```
 */
export function Providers({ children }: ProvidersProps): ReactElement {
  // NOTE: MutationObserver for debug timing removed - it causes jank during modal animation
  // by running expensive querySelector calls on every DOM change

  // Detect mobile once on mount
  const isMobile = useMemo(() => isMobileDevice(), []);

  // Memoize event handlers to maintain stable references
  const handleLogout = useCallback(() => {
    queryClient.clear();
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.replace('/');
    }
  }, []);

  // Memoize events object - dispatch custom events so components can react to modal state
  const events = useMemo(
    () => ({
      onAuthFlowClose: () => {
        // Dispatch event so components can clear loading state
        window.dispatchEvent(new CustomEvent('dynamic-auth-flow-close'));
      },
      onAuthFlowOpen: () => {
        // Dispatch event so components can react to modal being visible
        window.dispatchEvent(new CustomEvent('dynamic-auth-flow-open'));
      },
      onLogout: handleLogout,
    }),
    [handleLogout]
  );

  // Memoize overrides object
  const overrides = useMemo(
    () => ({
      evmNetworks: getEvmNetworks(),
    }),
    []
  );

  // Filter to hide external wallet options on mobile (social login only)
  // This only affects the login modal options, NOT embedded wallet functionality
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const walletsFilter = useCallback((wallets: any[]) => {
    // On mobile: return empty array to hide all external wallet options
    // On desktop: show all wallets
    return isMobile ? [] : wallets;
  }, [isMobile]);

  // Memoize the entire settings object for stable reference
  // EthereumWalletConnectors is required for embedded wallet UI display
  const dynamicSettings = useMemo(
    () => ({
      environmentId: dynamicEnvironmentId,
      // Always include EthereumWalletConnectors - needed for embedded wallet UI display
      walletConnectors: [EthereumWalletConnectors],
      walletConnectPreferredChains: networkEnv === 'testnet' ? ['eip155:43113' as const] : ['eip155:43114' as const],
      overrides,
      cssOverrides: dynamicCssOverrides,
      events,
      // Hide external wallet options on mobile - only show social login
      walletsFilter,
      // Legal: show TOS and Privacy Policy consent on the signing/login step
      termsOfServiceUrl: 'https://onicorn.io/terms',
      privacyPolicyUrl: 'https://onicorn.io/privacy',
    }),
    [overrides, events, walletsFilter]
  );

  return (
    <DynamicContextProvider settings={dynamicSettings}>
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <DynamicWagmiConnector>
            <AuthProvider>
              {children}
            </AuthProvider>
          </DynamicWagmiConnector>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicContextProvider>
  );
}
